/**
 * EscalationContext — "show me what actually caused this."
 *
 * The escalation card above it gives the kind, the AI's one-line concern, and
 * (for a screening flag) the question / answer / rule. That is the SUMMARY, and
 * it is frozen: `createAgentEscalation` dedupes on a stable key, so a lead who
 * re-phrases a disqualifying answer three times still has one row carrying
 * their FIRST wording. A recruiter deciding whether the flag is real cannot do
 * it from a snapshot — they need the exchange, in order, in the lead's own
 * words.
 *
 * So this fetches live: the messages around the triggering one (marked), plus —
 * when the wall blocked the agent's reply — the exact copy it refused and the
 * checks it failed, so the block can be judged rather than taken on faith.
 *
 * Collapsed by default (density bar), and it fetches only on first open: an
 * escalations tab with thirty rows must not fire thirty transcript reads.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ChevronDown, ChevronRight, Loader2, MessageSquareText, ShieldAlert, MoreHorizontal,
} from 'lucide-react';
import api from '../../../services/api';
import { timeShort, SENDER_LABEL, type ThreadMessage } from './shared';

interface ContextTurn {
  id: string;
  status: string;
  draft_body: string | null;
  final_body: string | null;
  error: string | null;
  failed_checks: Array<{ id: string; reason: string | null }>;
  created_at: string;
}

interface ContextPayload {
  messages: ThreadMessage[];
  truncated_before?: boolean;
  turn?: ContextTurn | null;
}

export default function EscalationContext({ escalationId }: { escalationId: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<ContextPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.get<ContextPayload>(`/api/crm/agent/escalations/${escalationId}/context`);
      setData(res.data);
    } catch (err) {
      console.error('[EscalationContext] fetch failed:', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [escalationId]);

  // Fetch on first open only. Re-opening shows what we already have — the
  // escalation is a past event, and the surrounding thread is not worth
  // re-reading on every toggle.
  useEffect(() => {
    if (open && !data && !loading && !failed) load();
  }, [open, data, loading, failed, load]);

  return (
    <div className="mt-1.5 sm:pl-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-primary-600 transition-colors duration-150 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300 sm:min-h-0"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {open ? 'Hide the conversation' : 'Show what caused this'}
      </button>

      {open && (
        <div className="mt-1.5 rounded-lg border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-900/40">
          {loading && (
            <p className="flex items-center gap-2 py-2 text-xs text-gray-500 dark:text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-600 dark:text-primary-400" />
              Loading the conversation…
            </p>
          )}
          {failed && (
            <p className="py-2 text-xs text-red-600 dark:text-red-400">
              Could not load the conversation.{' '}
              <button type="button" onClick={load} className="font-medium underline underline-offset-2">Try again</button>
            </p>
          )}

          {data && !loading && (
            <>
              {/* The blocked copy — for a guardrail_block this IS the cause,
                  and a reviewer must see the words, not just the verdict. */}
              {data.turn && (data.turn.status === 'suppressed' || data.turn.failed_checks.length > 0) && (
                <div className="mb-2 rounded-lg border border-red-200 bg-red-50/70 p-2 dark:border-red-900/50 dark:bg-red-900/15">
                  <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
                    <ShieldAlert className="h-3.5 w-3.5" /> The reply the wall refused
                  </p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-xs italic text-gray-700 dark:text-gray-200">
                    {data.turn.draft_body || '(nothing was written)'}
                  </p>
                  {data.turn.failed_checks.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {data.turn.failed_checks.map((c) => (
                        <li key={c.id} className="text-[11px] text-red-700 dark:text-red-300">
                          <span className="font-semibold">{c.id}</span>
                          {c.reason ? ` — ${c.reason}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                <MessageSquareText className="h-3.5 w-3.5" /> What was said
              </p>
              {data.truncated_before && (
                <p className="mb-1 flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                  <MoreHorizontal className="h-3.5 w-3.5" /> earlier messages not shown
                </p>
              )}
              {data.messages.length === 0 ? (
                <p className="py-1 text-xs text-gray-500 dark:text-gray-400">
                  No messages around this flag — it came from screening state rather than something the lead said.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {data.messages.map((m) => {
                    const isLead = m.sender === 'lead';
                    if (m.sender === 'system') {
                      return (
                        <li key={m.id} className="text-center text-[11px] text-gray-400 dark:text-gray-500">
                          {m.body} · {timeShort(m.occurred_at)}
                        </li>
                      );
                    }
                    return (
                      <li
                        key={m.id}
                        className={`rounded-lg px-2 py-1.5 ${m.is_trigger
                          ? 'bg-amber-50 ring-1 ring-amber-300 dark:bg-amber-900/20 dark:ring-amber-800'
                          : 'bg-gray-50 dark:bg-gray-800/60'}`}
                      >
                        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                          <span className={isLead ? 'text-primary-600 dark:text-primary-400' : ''}>
                            {SENDER_LABEL[m.sender] || m.sender}
                          </span>
                          <span className="font-normal normal-case tracking-normal">{timeShort(m.occurred_at)}</span>
                          {m.is_trigger && (
                            <span className="rounded-full bg-amber-200 px-1.5 font-semibold text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                              this is what triggered the flag
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-gray-800 dark:text-gray-200">{m.body}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
